/**
 * Helpers for reading job lifecycle fields written by the backend.
 * Every field is optional: jobs created before the backend added
 * terminalReason / heartbeatAt / attemptId must keep working.
 */

export interface JobTerminalReason {
  family?: string;
  message?: string;
  userAction?: string;
  technical?: string;
  stage?: string;
  attemptId?: string;
  at?: unknown;
}

export const FAILED_STATUSES = ['failed', 'job-failed'];
export const TERMINAL_STATUSES = ['complete', 'failed', 'job-failed', 'stopped'];

/** Heartbeat older than this on a nonterminal job means the worker likely died. */
export const HEARTBEAT_STALE_MS = 3 * 60 * 1000;

export function isFailedStatus(status: unknown): boolean {
  return typeof status === 'string' && FAILED_STATUSES.includes(status);
}

export function isTerminalStatus(status: unknown): boolean {
  return typeof status === 'string' && TERMINAL_STATUSES.includes(status);
}

/**
 * Convert a Firestore Timestamp, a serialized {seconds, nanoseconds} object,
 * a Date, epoch millis or an ISO string to epoch millis. Returns null if unknown.
 */
export function toMillis(value: any): number | null {
  if (value == null) { return null; }
  if (typeof value === 'number') { return Number.isFinite(value) ? value : null; }
  if (value instanceof Date) { return value.getTime(); }
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value.toMillis === 'function') {
    try { return value.toMillis(); } catch { return null; }
  }
  const seconds = value.seconds ?? value._seconds;
  if (typeof seconds === 'number') {
    const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
    return seconds * 1000 + Math.floor(nanos / 1e6);
  }
  return null;
}

export function getTerminalReason(job: any): JobTerminalReason | null {
  const reason = job?.terminalReason;
  if (!reason || typeof reason !== 'object') { return null; }
  const hasContent = ['family', 'message', 'userAction', 'technical'].some((k) => !!reason[k]);
  return hasContent ? reason as JobTerminalReason : null;
}

/** True when the job has a heartbeat, is nonterminal and the heartbeat is stale. */
export function isHeartbeatStale(job: any, now: number = Date.now()): boolean {
  if (!job || isTerminalStatus(job.status)) { return false; }
  const beat = toMillis(job.heartbeatAt);
  if (beat == null) { return false; }
  return now - beat > HEARTBEAT_STALE_MS;
}

export function hasHeartbeat(job: any): boolean {
  return toMillis(job?.heartbeatAt) != null;
}

/** Seconds between job creation and the last update (completion). */
export function jobDurationSeconds(job: any): number | null {
  const start = toMillis(job?.createdAt);
  const end = toMillis(job?.completedAt) ?? toMillis(job?.updatedAt);
  if (start == null || end == null || end < start) { return null; }
  return Math.round((end - start) / 1000);
}

/** Plain-text block users can paste into a support message. */
export function buildTechnicalDetails(job: any): string {
  const reason = getTerminalReason(job);
  const lines = [
    `jobId: ${job?.jobId ?? 'unknown'}`,
    `status: ${job?.status ?? 'unknown'}`,
    `jobType: ${job?.jobType ?? ''} ${job?.jobSubType ?? ''}`.trim(),
    `sport/site: ${job?.sport ?? ''}/${job?.site ?? ''}`,
    `attemptId: ${reason?.attemptId ?? job?.attemptId ?? 'n/a'}`,
    `attemptCount: ${job?.attemptCount ?? 'n/a'}`,
    `family: ${reason?.family ?? 'n/a'}`,
    `stage: ${reason?.stage ?? 'n/a'}`,
    `at: ${formatTime(reason?.at) ?? 'n/a'}`,
    `heartbeatAt: ${formatTime(job?.heartbeatAt) ?? 'n/a'}`,
    `updatedAt: ${formatTime(job?.updatedAt) ?? 'n/a'}`,
  ];
  if (reason?.message) { lines.push(`message: ${reason.message}`); }
  if (reason?.technical) { lines.push(`technical: ${reason.technical}`); }
  const exceptions = Array.isArray(job?.jobExceptionLog) ? job.jobExceptionLog : [];
  if (exceptions.length) { lines.push(`exceptions: ${exceptions.slice(-5).join(' | ')}`); }
  return lines.join('\n');
}

function formatTime(value: any): string | null {
  const ms = toMillis(value);
  return ms == null ? null : new Date(ms).toISOString();
}

/** Runtime / result fields that must not carry over into a retried job. */
const RETRY_STRIP_FIELDS = [
  'jobId', 'status', 'createdAt', 'updatedAt', 'completedAt',
  'jobProgressLog', 'jobExceptionLog', 'terminalReason', 'attemptId', 'attemptCount',
  'heartbeatAt', 'workerStartedAt', 'inputOrigin', 'bigQueryTableId', 'lineupsGenerated',
  'iterationCount', 'optoActiveRunId', 'optoStopReason', 'optoRunType', 'description',
  'lineupSummary', 'playerData', 'isSummaryZip', 'stoppedByUser', 'clientFailure',
  'userProjectionValues', 'draftGroup', 'universalBuilder',
];

/**
 * Build a job-creator snapshot from an existing job document so the creator
 * opens prefilled. The draft group is not stored on job docs, so the creator
 * reloads it and re-applies `retryProjectionValues`.
 */
export function buildRetrySnapshot(job: any): any {
  const snapshot: any = { ...job };
  RETRY_STRIP_FIELDS.forEach((k) => delete snapshot[k]);
  snapshot.status = 'building';
  snapshot.draftGroup = null;
  snapshot.retryOfJobId = job?.jobId ?? null;
  snapshot.retryProjectionValues = Array.isArray(job?.userProjectionValues) ? job.userProjectionValues : null;
  // Firestore Timestamps are not JSON safe; snapshots live in localStorage.
  return JSON.parse(JSON.stringify(snapshot));
}
